import {CBARProcessNode} from "./CBARProcessNode";
import {CBARFrame} from "../CBARFrame";
import {Mat, Point} from "mirada";
import {CBARPipeline, CBARPipelineTask} from "./CBARPipeline";
import * as gm from "gammacv";
import {CBARDebug} from "../../CBARContext";
import {matRelease, matRetain} from "../Utils";
import {euclideanDist} from "../Math";

type OpticalFlowPoint = {
    point:Point
    frameTime:number
    frameIndex:number
}

let OpticalFlowFeature_index = 0;

export class OpticalFlowFeature {
    constructor(private _currentPosition:OpticalFlowPoint, public cluster?:OpticalFlowCluster) {

    }

    public readonly id = OpticalFlowFeature_index++;
    public status?:number;
    public error?:number;
    public verified = 0;

    private _previousPosition?:OpticalFlowPoint;

    public get currentPosition() {
        return this._currentPosition.point;
    }

    public get previousPosition() {
        return this._previousPosition?.point;
    }

    public get direction() {
        if (!this._previousPosition) return undefined;
        return {x:this._currentPosition.point.x - this._previousPosition.point.x,
                y:this._currentPosition.point.y - this._previousPosition.point.y};
    }

    public update(point:OpticalFlowPoint) {
        this._previousPosition = this._currentPosition;
        this._currentPosition = point;
    }
}

let OpticalFlowCluster_index = 0;

export class OpticalFlowCluster {

    constructor(public roi:gm.Rect, public qualityLevel:number, public minFeatures=15) {

    }

    public readonly id = OpticalFlowCluster_index++;

    public features:OpticalFlowFeature[] = []
}

export type OpticalFlowResult = {
    points:OpticalFlowFeature[]
    removed:OpticalFlowFeature[]
}

export type OpticalFlowConfig = {
    maxPoints:number,
    qualityLevel:number,
    minDistance:number,
    windowSize:number,
    updateFrequency:number,
    findNewFrequency:number,
    cleanupFrequency:number,
}

const _default:OpticalFlowConfig = {
    maxPoints:80,
    qualityLevel:0.1,
    minDistance:5,
    windowSize:5,
    updateFrequency:1,
    findNewFrequency:60,
    cleanupFrequency:10
}

export class CBAROpticalFlowNode extends CBARProcessNode {

    private _prevFrame?:Mat;
    private _features:OpticalFlowFeature[] = [];
    private _clusters:OpticalFlowCluster[] = [];
    private _gotLastFeaturesIndex = 0;

    constructor(context:CBARPipeline, _config?:OpticalFlowConfig) {
        super(context)

        this.config = {..._default, ..._config};
    }

    public config:OpticalFlowConfig

    public trackRegion(cluster:OpticalFlowCluster) {
        this._clusters[cluster.id] = cluster;
        return cluster;
    }

    public untrackRegion(cluster:OpticalFlowCluster) {
        this._features = this._features.filter(f=>f.cluster !== cluster);
        delete this._clusters[cluster.id];
    }

    public get trackedRegions() {
        return this._clusters;
    }

    private getNewFeatures(frame:CBARFrame, numFeatures:number, cluster?:OpticalFlowCluster) : OpticalFlowFeature[] {

        let mask:Mat;

        //define valid regions:
        if (cluster && cluster.roi.area) {
            //todo:need to use polylines/fill here since it's (gm.Rect) a four point area not really a 2D ROI
            mask = cv.Mat.zeros(frame.greyscaleImage.rows, frame.greyscaleImage.cols, cv.CV_8UC1);
            const pointA = new cv.Point(cluster.roi.ax / this.pipeline.downsample,cluster.roi.ay / this.pipeline.downsample);
            const pointB = new cv.Point(cluster.roi.cx  / this.pipeline.downsample, cluster.roi.cy  / this.pipeline.downsample);

            //cv.pointPolygonTest();
            cv.rectangle(mask, pointA, pointB, [255,0,0,255], cv.FILLED);
        } else {
            mask = cv.Mat.ones(frame.greyscaleImage.rows, frame.greyscaleImage.cols, cv.CV_8UC1);
        }

        this._features.forEach(f=>{
            cv.circle(mask, new cv.Point(f.currentPosition.x / frame.context.downsample, f.currentPosition.y / frame.context.downsample),
                this.config.minDistance, [0,0,0,255], cv.FILLED);
        });

        let newPoints = new cv.Mat();
        cv.goodFeaturesToTrack(frame.greyscaleImage, newPoints, numFeatures, cluster ? cluster.qualityLevel : this.config.qualityLevel, this.config.minDistance, mask, this.config.windowSize);

        const newFeatures:OpticalFlowFeature[] = [];

        for (let i=0; i<newPoints.rows; i+=2) {
            const point = new cv.Point(newPoints.data32F[i] * frame.context.downsample, newPoints.data32F[i+1] * frame.context.downsample);
            const oflowPoint = {point, frameIndex:frame.index, frameTime:frame.time};
            const feature = new OpticalFlowFeature(oflowPoint, cluster);
            newFeatures.push(feature);
        }

        newPoints.delete();
        mask.delete();

        return newFeatures;
    }

    private status = new cv.Mat();
    private err = new cv.Mat();
    private prevPoints = new cv.Mat();
    private nextPoints = new cv.Mat();

    private updateFeatures(prevFrame:Mat, nextFrame:CBARFrame) {

        if (this.prevPoints.rows != this._features.length) {
            //console.count(`resizing points from ${this.prevPoints.rows} to ${this._features.length}`);
            this.prevPoints.delete();
            this.prevPoints = new cv.Mat(this._features.length, 2, cv.CV_32F);
        }

        for (let i=0, j=0; i<this._features.length; i++, j+=2) {
            const feature = this._features[i];
            this.prevPoints.data32F[j] = feature.currentPosition.x / nextFrame.context.downsample;
            this.prevPoints.data32F[j+1] = feature.currentPosition.y / nextFrame.context.downsample;
        }

        //Use these optimizations, with debug feedback:
        //normal without params 43fps desktop, 35fps iPhone
        //winSize from 21 to 7 went from 43fps to 51
        //maxLevel from 3 to 1 went from 43fps to 50
        //both winSize from 21 and 3 to 7 and 1 went from 43fps to 53fps
        //
        //Adding in Term Criteria changes 54-56 fps and 38-40fps iPhone
        const winSize = new cv.Size(13,13); //todo: adjust by using the accelerometer (raycast over time to get a vector)
        const maxLevel = 2;
        const termCriteria = new cv.TermCriteria(cv.TermCriteria_COUNT + cv.TermCriteria_EPS, 20, 0.02);
        cv.calcOpticalFlowPyrLK(prevFrame, nextFrame.greyscaleImage, this.prevPoints, this.nextPoints, this.status, this.err, winSize, maxLevel, termCriteria);

        let error = 0;
        for (let i=0, j=0; i < this._features.length; i++, j+=2) {
            const feature = this._features[i];
            feature.status = this.status.data[i];
            feature.error = this.err.data[i];
            error += feature.error!;

            if (feature.status) {
                const xPos = this.nextPoints.data32F[j] * nextFrame.context.downsample;
                const yPos = this.nextPoints.data32F[j+1] * nextFrame.context.downsample;
                this._features[i].update({point: {x:xPos, y:yPos}, frameIndex:nextFrame.index, frameTime:nextFrame.time});
            }
        }

        return error;
    }

    private async cleanupFeatures(features:OpticalFlowFeature[]) {
        //set status to 0 for bad ones
        //console.log("trying to cleanup bad features");

        const points = cv.Mat.zeros(features.length, 1, cv.CV_32FC3);
        const channels = 3;

        features.forEach((f,index)=>{
            const offset = index * channels;
            const direction = f.direction!;

            points.data32F[offset] = direction.x;
            points.data32F[offset + 1] = direction.y;
            points.data32F[offset + 2] = f.error!;
        });

        let mean = new cv.Mat();
        let standardDeviation = new cv.Mat();

        //console.log("calc mean CV_32FC4");
        cv.meanStdDev(points, mean, standardDeviation);

        const meanDistance = {x:mean.data64F[0], y:mean.data64F[1]};
        const stdDistance = {x:standardDeviation.data64F[0], y:standardDeviation.data64F[1]};
        const maxDistanceError = 3.0 * euclideanDist(stdDistance, {x:0, y:0});
        const stdError = standardDeviation.data64F[2];
        const maxError = 10.0 * stdError;

        let destroyed = 0;
        features.forEach(f=>{
            const distanceError = euclideanDist(f.direction!, meanDistance);
            if (distanceError > maxDistanceError || f.error! > maxError) {
                f.status = 0;//invalidate
                f.verified = 0;
                destroyed ++;
            } else {
                f.verified ++;
            }
        })

        points.delete();

        mean.delete();
        standardDeviation.delete();

        return destroyed;
    }

    public get features() {
        const statusFiltered = this._features.filter(f=>f.status);
        const verifiedFiltered = statusFiltered.filter(f=>f.verified);
        return verifiedFiltered.length > 3 ? verifiedFiltered : statusFiltered;
    }

    private _needsCleanup = false;

    update(frame:CBARFrame):void {
        if (frame.index % this.config.updateFrequency) return;

        if (this._needsCleanup) {
            this._needsCleanup = false;
            this._features = this._features.filter(f=>f.status);
        }

        const toGrab = Math.floor(this.config.maxPoints - this._features.length);
        const minToGrab = Math.floor(this.config.maxPoints / 5);
        if (toGrab > minToGrab && (frame.index - this._gotLastFeaturesIndex) > this.config.findNewFrequency) {
            this._features = this._features.concat(this.getNewFeatures(frame, toGrab));
            this._gotLastFeaturesIndex = frame.index;
            //console.log(`Has ${this._features.length} features`)
        }

        this._clusters.forEach(cluster=>{
            if (cluster.features.length < cluster.minFeatures) {
                const numNeeded = cluster.minFeatures - cluster.features.length;
                const features = this.getNewFeatures(frame, numNeeded * 2, cluster);
                cluster.features = cluster.features.concat(features);
                this._features = this._features.concat(features);
            }
        })

        if (this._prevFrame && this._features.length > 1) {
            const error = this.updateFeatures(this._prevFrame, frame);
            if (error === 0) return; //invalid, probably two identical frames
        }

        //remove bad features and send result;
        const goodFeatures = this._features.filter(f=>f.status && f.direction);

        if (goodFeatures.length) {
            goodFeatures.length > 5 && (frame.index % this.config.cleanupFrequency === 0) && this.cleanupFeatures(goodFeatures).then(numDestroyed=>{
                this._needsCleanup = numDestroyed > 0;
            });

            const removedFeatures = this._features.filter(f=>f.status===0);
            this.pipeline.completeTask<OpticalFlowResult>(CBARPipelineTask.OpticalFlow, ()=>{
                return {points:goodFeatures, removed:removedFeatures};
            })
        }

        this._features = goodFeatures;
        if (this._prevFrame) {
            matRelease(this._prevFrame);
        }
        this._prevFrame = matRetain(frame.greyscaleImage);
    }

    debug(canvas:HTMLCanvasElement):void {
        if (CBARDebug.OpticalFlow === (this.pipeline.config.debug & CBARDebug.OpticalFlow)) {
            this._features.forEach(feature=>{
                gm.canvasDrawCircle(canvas, [feature.currentPosition.x, feature.currentPosition.y], 3,  feature.verified ? 'rgba(0, 255, 0, 1)' : 'rgba(255, 255, 0, 1)');
            })
        }
    }

    destroy() {
        if (this._prevFrame) {
            matRelease(this._prevFrame);
        }
        this.status.delete();
        this.err.delete();
        this.prevPoints.delete();
        this.nextPoints.delete();
    }
}